import React from 'react';
import {
  BACK_ORDER_ETA_DISCLAIMER,
  COLLECTION_TODAY_TITLE,
  DEFAULT_TITLE,
  ORDER_COMPLETE_SUBTITLE,
  ORDER_COMPLETE_TITLE,
} from '../constants';
import { TrackingResult } from '../TrackingResult';
import { styles } from './TrackingResults.css';
import { ShipmentStatusLabel } from '../ShipmentStatusLabel';
import {
  ShipmentStatus,
  FulfilmentType,
  OrderShipment,
  TrackingOrder,
} from '@jbhi-fi/apis/src/orderTrackingThemeApi/types';
import { OrderShipmentTransformer } from '../transformers/OrderShipmentTransformer';
import { CarouselTabs, CarouselTabsProps } from '../../CarouselTabs/CarouselTabs';
import { MinimalTab } from '../../CarouselTabs/MinimalTab/MinimalTab';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { useAlgoliaStore } from '../../hooks/useAlgoliaStore/useAlgoliaStore';
import { AlgoliaStoreData } from '@jbhi-fi/apis/src/algolia/types/AlgoliaStoreData';
import { getTimeWindow } from '@jbhi-fi/utils/src/date/getFriendlyFormattedDate';
import { isBackOrderConfirmedWithEta, isBackOrderEtaPassed, isBackOrderReceived } from '../backOrderHelpers';
import { ProductInformationProps } from '../ProductInformation/ProductInformationCard';

export type TrackingResultsProps = {
  order: TrackingOrder;
};

const getCollectionDay = (status: string, isStoreOpen: boolean, shipmentEta?: string): string => {
  if (!shipmentEta) return 'To be confirmed';

  if (status === 'AwaitingCollection') return isStoreOpen ? 'Today' : 'Tomorrow';

  return shipmentEta;
};

const getResultHeading = (
  status: ShipmentStatus,
  shipmentEta: string,
  fulfilmentType: FulfilmentType,
  isStoreOpen: boolean = false,
  shipment?: OrderShipment
) => {
  if (status === 'Delivered' || status === 'Collected')
    return { title: ORDER_COMPLETE_TITLE, subtitle: ORDER_COMPLETE_SUBTITLE };
  if (fulfilmentType === 'CustomerCollect')
    return {
      title: COLLECTION_TODAY_TITLE,
      subtitle: getCollectionDay(status, isStoreOpen, shipmentEta),
    };

  if (shipment && isBackOrderEtaPassed(shipment)) {
    return { title: undefined, subtitle: undefined, disclaimer: undefined };
  }

  if (shipment && isBackOrderConfirmedWithEta(shipment)) {
    return { title: DEFAULT_TITLE, subtitle: shipmentEta, disclaimer: BACK_ORDER_ETA_DISCLAIMER };
  }

  return { title: DEFAULT_TITLE, subtitle: shipmentEta };
};

const stripBackOrderAlerts = (lineItems: Array<ProductInformationProps>): Array<ProductInformationProps> =>
  lineItems.map(item => (item.alert?.type === 'Message' ? { ...item, alert: undefined } : item));

const getTabHeader = (type: FulfilmentType, index: number) => {
  switch (type) {
    case 'DigitalDelivery':
      return 'Digital Download';
    case 'CustomerCollect':
      return `Collection #${index + 1}`;
    default:
      return `Shipment #${index + 1}`;
  }
};

const getTabStatusLabel = (shipment: OrderShipment) => {
  if (isBackOrderEtaPassed(shipment)) {
    return { label: 'Delayed', icon: 'Error' as const, color: '#D4760A' };
  }
  if (isBackOrderReceived(shipment)) {
    return { label: OrderShipmentTransformer.toFriendlyShipmentStatus(shipment.status) };
  }
  return OrderShipmentTransformer.toShipmentStatusProps(shipment);
};

const getTabs = (orderNumber: string, shipments: Array<OrderShipment>): CarouselTabsProps['tabs'] => {
  return shipments.map((shipment, i) => {
    const parsedProducts = OrderShipmentTransformer.toProductInformationCardsProps(shipment.lineItems, {
      isDigital: shipment.type === 'DigitalDelivery',
      isCancelled: shipment.status === 'Cancelled',
    });
    const shipmentEta = shipment.isTracked
      ? OrderShipmentTransformer.toShipmentEta(shipment)
      : getTimeWindow(shipment.shipmentEta, 'days', 5);

    const lineItems = isBackOrderReceived(shipment) ? stripBackOrderAlerts(parsedProducts) : parsedProducts;

    return {
      renderLabel: props => (
        <MinimalTab
          {...props}
          className={styles.headerContainer}
          label={
            <>
              {getTabHeader(shipment.type, i)}
              <div className={styles.subHeading}>
                <ShipmentStatusLabel {...getTabStatusLabel(shipment)} />
              </div>
            </>
          }
        />
      ),
      contents: (
        <TrackingResult
          orderNumber={orderNumber}
          {...getResultHeading(
            shipment.status,
            shipmentEta,
            shipment.type,
            shipment.messaging?.store?.isOpen,
            shipment
          )}
          shipment={shipment}
          lineItems={lineItems}
          renderingOrder={['whatsComing', 'fulfilment', 'history']}
        />
      ),
    };
  });
};

export const TrackingResults = ({ order }: TrackingResultsProps) => {
  const isMobile = !useBreakpoint('md', 'up');
  const { stores } = useAlgoliaStore({
    locationIds: order.collections?.map(collection => collection.messaging?.locationId || ''),
  });

  const allShipments = [
    ...OrderShipmentTransformer.toShipmentInformation(order.digitalDeliveries ?? []),
    ...OrderShipmentTransformer.toShipmentInformation(order.deliveries ?? []),
    ...OrderShipmentTransformer.toShipmentInformation(order.collections ?? [], stores as Array<AlgoliaStoreData>),
  ] as Array<OrderShipment>;
  const tabs = getTabs(order.orderNumber, allShipments);
  const hasMultipleTabs = tabs.length > 1;
  const shipmentEta = allShipments[0].isTracked
    ? OrderShipmentTransformer.toShipmentEta(allShipments[0])
    : getTimeWindow(allShipments[0].shipmentEta, 'days', 5);

  return (
    <div data-testid="order-tracking-results" className={styles.container}>
      <h2 className={styles.title({ hasMultipleShipments: allShipments.length > 1 })}>Track my order</h2>

      {hasMultipleTabs ? (
        <>
          <div className={styles.multipleShipmentsTag}>Your order is coming in {allShipments.length} shipments</div>
          <CarouselTabs tabs={tabs} slidesPerView={Math.min(tabs.length, isMobile ? 2.5 : 4.5)} />
        </>
      ) : (
        <TrackingResult
          orderNumber={order.orderNumber}
          {...getResultHeading(
            allShipments[0].status,
            shipmentEta,
            allShipments[0].type,
            allShipments[0].messaging?.store?.isOpen,
            allShipments[0]
          )}
          shipment={allShipments[0]}
          lineItems={(() => {
            const parsed = OrderShipmentTransformer.toProductInformationCardsProps(allShipments[0].lineItems, {
              isCancelled: allShipments[0].status === 'Cancelled',
            });
            return isBackOrderReceived(allShipments[0]) ? stripBackOrderAlerts(parsed) : parsed;
          })()}
        />
      )}
    </div>
  );
};
